import React from 'react';
import Util from '../../../../shared/util/util';
import {Table} from '@/web-components';
import {TaskIndicatorsColumn} from './columns/TaskIndicatorsColumn';
import LabelGroup from '../../../../shared/components/labels/label_group';
import AssignedTableTile from '../../../../shared/components/table-tiles/assigned_table_tile';
import {ESTIMATION_UNIT, MODULE_TYPES} from '../../../../../constants';
import {hasModule} from '../../../../shared/util/ModuleUtil';
import {DeprecatedProjectIndicatorNoFragment} from '../../../../shared/components/project-indicator/DeprecatedProjectIndicator';
import {hasFeatureFlag} from '../../../../shared/util/FeatureUtil';
import {noop} from 'lodash';

export const taskRow = (intl, task, showTaskModal, isSharedView, currency): {id: string; content: React.ReactNode}[] => {
	const customFields =
		(hasModule(MODULE_TYPES.CUSTOM_FIELDS) || isSharedView) && task.reportCustomFieldValues
			? [...task.reportCustomFieldValues].sort((a, b) => (a.displayName > b.displayName ? 1 : -1))
			: [];

	const labels = task.labels?.map(l => {
		return {label: l};
	});

	const hasFinancialCategoriesUpdate = hasFeatureFlag('financial_categories_update');

	const columns: {id: string; content: React.ReactNode; showCondition?: boolean}[] = [
		{
			id: 'name',
			content: (
				<Table.Column.Cell
					title={task.name}
					onClick={() => !isSharedView && !task.project.readOnlyAccess && showTaskModal(task.companyTaskId)}
				>
					{`T${task.companyTaskId} ${task.name}`}
				</Table.Column.Cell>
			),
		},
		{id: 'indicator', content: <TaskIndicatorsColumn task={task} />},
		{
			id: 'project',
			content: (
				<Table.Column.Cell title={task.project.name}>
					{task.project.companyProjectId && task.project.projectColor ? (
						<DeprecatedProjectIndicatorNoFragment
							project={task.project}
							disableLink={isSharedView || task.project.readOnlyAccess}
							showProjectName
							clearSubPath
						/>
					) : (
						task.project.name
					)}
				</Table.Column.Cell>
			),
		},
		{
			id: 'project_deadline',
			content: (
				<Table.Column.Cell>
					{task.project.deadline &&
						intl.formatDate(task.project.deadline, {
							year: 'numeric',
							month: 'short',
							day: 'numeric',
							timeZone: 'UTC',
						})}
				</Table.Column.Cell>
			),
		},
		{
			id: 'project_stage',
			content: <Table.Column.Cell>{Util.getProjectStageTranslation(task.project.stage, intl)}</Table.Column.Cell>,
		},
		{id: 'rateCard', content: <Table.Column.Cell>{task.project.rateCard?.name}</Table.Column.Cell>},
		{
			id: 'client',
			content: <Table.Column.Cell title={task.project.client?.name}>{task.project.client?.name}</Table.Column.Cell>,
		},
		{
			id: 'task_startDate',
			content: (
				<Table.Column.Cell>
					{task.dates &&
						intl.formatDate(task.dates.start, {
							year: 'numeric',
							month: 'short',
							day: 'numeric',
							timeZone: 'UTC',
						})}
				</Table.Column.Cell>
			),
		},
		{
			id: 'task_endDate',
			content: (
				<Table.Column.Cell>
					{task.dates &&
						intl.formatDate(task.dates.deadline, {
							year: 'numeric',
							month: 'short',
							day: 'numeric',
							timeZone: 'UTC',
						})}
				</Table.Column.Cell>
			),
		},
		{
			id: 'task_createdAt',
			content: (
				<Table.Column.Cell>
					{task.dates &&
						intl.formatDate(task.dates.createdAt, {
							year: 'numeric',
							month: 'short',
							day: 'numeric',
							timeZone: 'UTC',
						})}
				</Table.Column.Cell>
			),
		},
		{id: 'role', content: <Table.Column.Cell>{task.role?.name}</Table.Column.Cell>},
		{
			id: 'assignees',
			content: (
				<Table.Column.Cell>
					<AssignedTableTile
						disabled={true}
						assignablePersons={[]}
						assignedPersons={task.assignees}
						assignPerson={noop}
						unassignPerson={noop}
						useTableStyling={true}
						isMultiSelect={false}
						id={task.id}
						key={'assigned-person'}
						task={task}
						maxDisplayed={5}
						cy={'assigned-person'}
						isClientUser={false}
						intl={intl}
					/>
				</Table.Column.Cell>
			),
		},
		{id: 'status', content: <Table.Column.Cell>{task.status?.name}</Table.Column.Cell>},
		{id: 'phase', content: <Table.Column.Cell>{task.phase?.name}</Table.Column.Cell>},
		{
			id: 'phase_startDate',
			content: (
				<Table.Column.Cell>
					{task.phase?.dates?.start &&
						intl.formatDate(task.phase.dates.start, {
							year: 'numeric',
							month: 'short',
							day: 'numeric',
							timeZone: 'UTC',
						})}
				</Table.Column.Cell>
			),
		},
		{
			id: 'phase_endDate',
			content: (
				<Table.Column.Cell>
					{task.phase?.dates?.deadline &&
						intl.formatDate(task.phase.dates.deadline, {
							year: 'numeric',
							month: 'short',
							day: 'numeric',
							timeZone: 'UTC',
						})}
				</Table.Column.Cell>
			),
		},
		{id: 'sprint', content: <Table.Column.Cell>{task.sprint?.name}</Table.Column.Cell>},
		{
			id: 'sprint_startDate',
			content: (
				<Table.Column.Cell>
					{task.sprint &&
						task.sprint.dates &&
						intl.formatDate(task.sprint.dates.start, {
							year: 'numeric',
							month: 'short',
							day: 'numeric',
							timeZone: 'UTC',
						})}
				</Table.Column.Cell>
			),
		},
		{
			id: 'sprint_endDate',
			content: (
				<Table.Column.Cell>
					{task.sprint &&
						task.sprint.dates &&
						intl.formatDate(task.sprint.dates.deadline, {
							year: 'numeric',
							month: 'short',
							day: 'numeric',
							timeZone: 'UTC',
						})}
				</Table.Column.Cell>
			),
		},
		{id: 'progress', content: <Table.Column.Cell>{`${task.progress}%`}</Table.Column.Cell>},
		{
			id: 'estimate_hours',
			content: (
				<Table.Column.Cell>
					{task.project.estimationUnit === ESTIMATION_UNIT.HOURS &&
						Util.convertMinutesToFullHour(task.work.estimate, intl)}
				</Table.Column.Cell>
			),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'estimate_points',
			content: (
				<Table.Column.Cell>
					{task.project.estimationUnit === ESTIMATION_UNIT.POINTS &&
						`${intl.formatNumber(task.work.estimate, {format: 'always_two_decimal'})}p`}
				</Table.Column.Cell>
			),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'projected',
			content: (
				<Table.Column.Cell>
					{task.reported && Util.convertMinutesToFullHour(task.reported.projected, intl)}
				</Table.Column.Cell>
			),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'reported',
			content: (
				<Table.Column.Cell>
					{task.reported && Util.convertMinutesToFullHour(task.reported.time, intl)}
				</Table.Column.Cell>
			),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'remaining_hours',
			content: (
				<Table.Column.Cell>
					{task.work &&
						task.project.estimationUnit === ESTIMATION_UNIT.HOURS &&
						Util.convertMinutesToFullHour(task.work.remaining, intl)}
				</Table.Column.Cell>
			),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'remaining_points',
			content: (
				<Table.Column.Cell>
					{task.work &&
						task.project.estimationUnit === ESTIMATION_UNIT.POINTS &&
						`${intl.formatNumber(task.work.remaining, {format: 'always_two_decimal'})}p`}
				</Table.Column.Cell>
			),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'difference_hours',
			content: (
				<Table.Column.Cell>
					{task.work &&
						task.project.estimationUnit === ESTIMATION_UNIT.HOURS &&
						Util.convertMinutesToFullHour(task.work.estimate - task.reported.time, intl)}
				</Table.Column.Cell>
			),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'difference_points',
			content: (
				<Table.Column.Cell>
					{task.work &&
						task.project.estimationUnit === ESTIMATION_UNIT.POINTS &&
						`${intl.formatNumber(task.work.estimate - task.reported.time / task.project.minutesPerEstimationPoint, {
							format: 'always_two_decimal',
						})}p`}
				</Table.Column.Cell>
			),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'planned_revenue',
			content: (
				<Table.Column.Cell>
					{task.financial?.revenue &&
						Util.GetFormattedCurrencySymbol(
							currency,
							intl.formatNumber(task.financial.revenue.planned, {format: 'always_two_decimal'})
						)}
				</Table.Column.Cell>
			),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'actual_revenue',
			content: (
				<Table.Column.Cell>
					{task.financial?.revenue &&
						Util.GetFormattedCurrencySymbol(
							currency,
							intl.formatNumber(task.financial.revenue.actual, {format: 'always_two_decimal'})
						)}
				</Table.Column.Cell>
			),
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'hours-estimated',
			content: (
				<Table.Column.Cell>
					{task.project.estimationUnit === ESTIMATION_UNIT.HOURS &&
						Util.convertMinutesToFullHour(task.financialNumbers.scopeApprovedMinutes, intl)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'hours-actual',
			content: (
				<Table.Column.Cell>
					{Util.convertMinutesToFullHour(
						task.financialNumbers.billableActualMinutes + task.financialNumbers.nonBillableActualMinutes,
						intl
					)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'hours-actual-billable',
			content: (
				<Table.Column.Cell>
					{Util.convertMinutesToFullHour(task.financialNumbers.billableActualMinutes, intl)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'hours-actual-non-billable',
			content: (
				<Table.Column.Cell>
					{Util.convertMinutesToFullHour(task.financialNumbers.nonBillableActualMinutes, intl)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'remaining-work-hours',
			content: (
				<Table.Column.Cell>
					{task.project.estimationUnit === ESTIMATION_UNIT.HOURS &&
						Util.convertMinutesToFullHour(task.financialNumbers.remainingMinutes, intl)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'projected-total-hours',
			content: (
				<Table.Column.Cell>
					{Util.convertMinutesToFullHour(task.financialNumbers.totalTimeAtCompletion, intl)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'estimated-points',
			content: (
				<Table.Column.Cell>
					{task.project.estimationUnit === ESTIMATION_UNIT.POINTS &&
						`${intl.formatNumber(task.work.estimate, {format: 'always_two_decimal'})}p`}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'remaining-work-points',
			content: (
				<Table.Column.Cell>
					{task.work &&
						task.project.estimationUnit === ESTIMATION_UNIT.POINTS &&
						`${intl.formatNumber(task.work.remaining, {format: 'always_two_decimal'})}p`}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'estimated-billable-value-of-service',
			content: (
				<Table.Column.Cell>
					{task.financialNumbers &&
						Util.GetFormattedCurrencySymbol(
							currency,
							intl.formatNumber(task.financialNumbers.billablePlannedTimeAndExpenses || 0, {
								format: 'always_two_decimal',
							})
						)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'actual-billable-value-of-service',
			content: (
				<Table.Column.Cell>
					{task.financialNumbers &&
						Util.GetFormattedCurrencySymbol(
							currency,
							intl.formatNumber(task.financialNumbers.billableActualTimeAndExpenses || 0, {
								format: 'always_two_decimal',
							})
						)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'remaining-work-billable',
			content: (
				<Table.Column.Cell>
					{task.financialNumbers &&
						Util.GetFormattedCurrencySymbol(
							currency,
							intl.formatNumber(task.financialNumbers.billableForecastTimeAndExpensesToComplete || 0, {
								format: 'always_two_decimal',
							})
						)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'projected-total-billable',
			content: (
				<Table.Column.Cell>
					{task.financialNumbers &&
						Util.GetFormattedCurrencySymbol(
							currency,
							intl.formatNumber(task.financialNumbers.billableTotalTimeAndExpensesAtCompletion || 0, {
								format: 'always_two_decimal',
							})
						)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'estimated-revenue',
			content: (
				<Table.Column.Cell>
					{task.financialNumbers &&
						Util.GetFormattedCurrencySymbol(
							currency,
							intl.formatNumber(task.financialNumbers.plannedRevenue || 0, {
								format: 'always_two_decimal',
							})
						)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'actual-revenue',
			content: (
				<Table.Column.Cell>
					{task.financialNumbers &&
						Util.GetFormattedCurrencySymbol(
							currency,
							intl.formatNumber(task.financialNumbers.actualRevenue || 0, {
								format: 'always_two_decimal',
							})
						)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'remaining-work-revenue',
			content: (
				<Table.Column.Cell>
					{task.financialNumbers &&
						Util.GetFormattedCurrencySymbol(
							currency,
							intl.formatNumber(task.financialNumbers.totalForecastRevenueToComplete || 0, {
								format: 'always_two_decimal',
							})
						)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'projected-total-revenue',
			content: (
				<Table.Column.Cell>
					{task.financialNumbers &&
						Util.GetFormattedCurrencySymbol(
							currency,
							intl.formatNumber(task.financialNumbers.totalRevenueRecognition || 0, {
								format: 'always_two_decimal',
							})
						)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'projected-over-under',
			content: (
				<Table.Column.Cell>
					{task.financialNumbers &&
						Util.GetFormattedCurrencySymbol(
							currency,
							intl.formatNumber(task.financialNumbers.surplus || 0, {format: 'always_two_decimal'})
						)}
				</Table.Column.Cell>
			),
			showCondition: hasFinancialCategoriesUpdate,
		},
		{
			id: 'approved',
			content: (
				<Table.Column.Cell>
					{task.approved ? intl.formatMessage({id: 'common.yes'}) : intl.formatMessage({id: 'common.no'})}
				</Table.Column.Cell>
			),
		},
		{
			id: 'labels',
			content: (
				<Table.Column.Cell>
					<LabelGroup labels={labels} noWrap={true} />
				</Table.Column.Cell>
			),
		},
		...customFields.map(customField => ({
			id: customField.key,
			content: <Table.Column.Cell>{customField.value}</Table.Column.Cell>,
		})),
	];

	return columns.filter(item => item.showCondition ?? true);
};
